import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Global avatar/profile-pic magnifier + mini profile-peek card.
// Tap any character avatar → it pops up enlarged with the creator's name and a
// Follow button. Tap the backdrop (or Esc) → it disappears.
export const ImageZoom = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null); // { src, name, cid }
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onClick = (e) => {
      const img = e.target.closest && e.target.closest("img");
      if (!img) return;
      if (img.closest('[data-testid="tour-overlay"]')) return;
      if (img.closest('[data-testid="avatar-zoom-overlay"]')) return;
      const url = img.currentSrc || img.src || "";
      const tagged = img.hasAttribute("data-zoom") || img.hasAttribute("data-zoom-name");
      // Only intercept images explicitly tagged as zoomable avatars. Matching by URL
      // (e.g. "cyborg"/"/creators/") wrongly hijacked card thumbnails and blocked their <Link> navigation.
      if (!tagged) return;
      e.preventDefault();
      e.stopPropagation();
      setData({
        src: url,
        name: img.getAttribute("data-zoom-name") || img.alt || "",
        cid: img.getAttribute("data-zoom-cid") || "",
      });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setData(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    setFollowing(false);
    if (data?.cid && user) {
      api.get(`/follow/${data.cid}`).then(({ data: f }) => setFollowing(!!f.following)).catch(() => {});
    }
  }, [data, user]);

  const toggleFollow = async (e) => {
    e.stopPropagation();
    if (!user) { setData(null); nav("/auth"); return; }
    if (!data?.cid || busy) return;
    setBusy(true);
    try {
      if (following) { await api.delete(`/follow/${data.cid}`); setFollowing(false); }
      else { await api.post("/follow", { channel_id: data.cid }); setFollowing(true); toast.success(`Following ${data.name || "creator"}`); }
    } catch (_) { toast.error("Couldn't update follow"); }
    finally { setBusy(false); }
  };

  if (!data) return null;

  return (
    <div onClick={() => setData(null)} data-testid="avatar-zoom-overlay"
      className="zoom-fade fixed inset-0 z-[120] grid cursor-zoom-out place-items-center bg-black/85 p-6 backdrop-blur-md">
      <div onClick={(e) => e.stopPropagation()} className="zoom-pop flex cursor-default flex-col items-center gap-4">
        <img src={data.src} alt={data.name} data-testid="avatar-zoom-img"
          className="max-h-[70vh] max-w-[86vw] rounded-3xl object-contain ring-2 ring-[#00FFC2]/50 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.95)] sm:max-w-[460px]" />
        {data.name && (
          <div className="flex flex-col items-center gap-3" data-testid="avatar-zoom-card">
            <p className="text-center font-display text-xl font-black tracking-tight text-white" data-testid="avatar-zoom-name">{data.name}</p>
            {data.cid && (
              <button onClick={toggleFollow} disabled={busy} data-testid="avatar-zoom-follow"
                className={`flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold transition-all hover:scale-105 disabled:opacity-60 ${following ? "bg-white/10 text-white ring-1 ring-white/20" : "bg-[#00FFC2] text-black"}`}>
                {following ? <><Check size={15} /> Following</> : <><UserPlus size={15} /> Follow</>}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
